/*
 * Copyright (c) 2019 Signum+ Software & Facility GmbH
 * Author: jahn@signum-plus.at
 */

import { Big } from 'big.js';
import { CoreErrorKey } from '../key/core-error.key';
import { CoreFormatterKey } from '../key/core-formatter.key';
import { ApplicationError } from '../model/application-error.model';
import { NumberFormatter, NumberFormatterOptions } from './number.formatter';

export class PercentFormatter extends NumberFormatter {

  constructor(options?: NumberFormatterOptions) {
    super(PercentFormatter.setupOptions(options));
  }

  format(value: number | string | Big): string {
    if (value === undefined || value === null || value === '') {
      return super.format(value);
    }
    try {
      return super.format(new Big(value).times(100));
    } catch (error) {
      throw new ApplicationError({
        key: CoreErrorKey.FORMAT_ERROR,
        arguments: { formatterKey: CoreFormatterKey.PERCENT, options: this.options, value },
        message: `Format value error: Value '${value}' invalid (${CoreFormatterKey.PERCENT})`
      });
    }
  }

  parse(value: string): number | string | Big {
    try {
      const result = super.parse(value);
      if (result === undefined || result === null) {
        return result;
      }
      switch (this.options.parseTo) {
        case 'number':
          return (result as number) / 100;
        case 'string':
          return new Big(result).div(100).toString();
        case 'big':
          return (result as Big).div(100);
      }
    } catch (error) {
      throw new ApplicationError({
        key: CoreErrorKey.PARSE_ERROR,
        arguments: { formatterKey: CoreFormatterKey.PERCENT, options: this.options, value },
        message: `Parse value error: Value '${value}' invalid (${CoreFormatterKey.PERCENT})`
      });
    }
  }

  clone(options: NumberFormatterOptions): PercentFormatter {
    return new PercentFormatter(Object.assign({}, this.options, options));
  }

  static setupOptions(options: NumberFormatterOptions): NumberFormatterOptions {
    if (!options || options.suffix === undefined) {
      return Object.assign({}, options, { suffix: '%' });
    }
    return options;
  }
}
